import * as sdkTypes from "@supernovaio/sdk"
import { createRequire } from "node:module"
import { HttpsProxyAgent } from "https-proxy-agent"

import { apiUrlForEnvironment, getTargetEnv } from "../types/environment.js"

const require = createRequire(import.meta.url)
const sdkProvider = require("@supernovaio/sdk") as typeof sdkTypes

export interface DefaultDesignSystemFlags {
  apiKey: string
  apiUrl?: string
  brandId?: string
  designSystemId: string
  proxyUrl?: string
  themeId?: string
}

export async function getWritableVersion(flags: DefaultDesignSystemFlags): Promise<{
  brand: null | sdkTypes.Brand
  designSystem: sdkTypes.DesignSystem
  id: sdkTypes.RemoteWorkspaceVersionIdentifier
  instance: sdkTypes.Supernova
  theme: null | sdkTypes.TokenTheme
  version: sdkTypes.DesignSystemVersion
}> {
  if (!flags.apiKey || flags.apiKey.length === 0) {
    throw new Error(`API key must not be empty`)
  }

  if (!flags.designSystemId || flags.designSystemId.length === 0) {
    throw new Error(`Design System ID must not be empty`)
  }

  // Create instance for prod / dev
  const apiUrl = flags.apiUrl && flags.apiUrl.length > 0 ? flags.apiUrl : apiUrlForEnvironment(getTargetEnv())
  const instance = new sdkProvider.Supernova(flags.apiKey, {
    apiUrl,
    customProxyLib: flags.proxyUrl ? new HttpsProxyAgent(flags.proxyUrl) : null,
  })

  const designSystem = await instance.designSystems.designSystem(flags.designSystemId)
  if (!designSystem) {
    throw new Error(`Design system ${flags.designSystemId} not found or not available under provided API key`)
  }

  const version = await instance.versions.getActiveVersion(flags.designSystemId)
  if (!version) {
    throw new Error(
      `Design system  ${flags.designSystemId} writable version not found or not available under provided API key`,
    )
  }

  const id: sdkTypes.RemoteWorkspaceVersionIdentifier = {
    designSystemId: flags.designSystemId,
    versionId: version.id,
    workspaceId: designSystem.workspaceId,
  }

  let brand: null | sdkTypes.Brand = null
  if (flags.brandId) {
    const brands = await instance.brands.getBrands(id)
    brand = brands.find(brand => brand.id === flags.brandId || brand.idInVersion === flags.brandId) ?? null
    if (!brand) {
      throw new Error(`Brand ${flags.brandId} not found in specified design system`)
    }
  }

  let theme: null | sdkTypes.TokenTheme = null
  if (flags.themeId) {
    const themes = await instance.tokens.getTokenThemes(id)
    theme = themes.find(theme => theme.id === flags.themeId || theme.idInVersion === flags.themeId) ?? null
    if (!theme) {
      throw new Error(`Theme ${flags.themeId} not found in specified brand`)
    }
  }

  return { brand, designSystem, id, instance, theme, version }
}
